// Окно «Назначить ведущего» (038: contracts/api.md; research Р4–Р8; LEAD-02 – LEAD-08). Ведущий —
// ведущий протокола, ответственный за его ведение: пользователь с ролью «Ведущий» и областью
// документа (014). Поиск — пользователи и люди справочника без учётной записи по ФИО и почте;
// смена — со сверкой «кого окно считает ведущим» (MIN-025) и «Отменить»; создание пользователя-
// ведущего — одним действием администратора клиента.
import { z } from '../kernel/zod.ts';
import { IdSchema } from '../kernel/common.ts';
import { EmailInputSchema, FullNameInputSchema } from '../access/user.ts';
import { EmailStandingSchema } from '../access/email-policy.ts';
import { LOOKUP_QUERY_MAX, LOOKUP_QUERY_MIN, ProtocolLeadSchema } from './fields.ts';

/** Строк в каждом разделе окна (FR-005). */
export const LEAD_LOOKUP_LIMIT = 8;

/** Что останется у прежнего ведущего после смены (FR-009; research Р6). */
export const leadAfterAccess = ['edit', 'zone', 'read', 'none'] as const;
export type LeadAfterAccess = (typeof leadAfterAccess)[number];
export const LeadAfterAccessSchema = z.enum(leadAfterAccess);

export const leadAfterAccessText = {
  edit: 'сможет править протокол',
  zone: 'сможет править пункты своей зоны',
  read: 'сможет только читать протокол',
  none: 'потеряет доступ к протоколу',
} as const satisfies Record<LeadAfterAccess, string>;

/** GET /api/protocols/{id}/lead: ведущий и что у него останется; null — ведущего нет. */
export const LeadStateSchema = z.object({
  lead: ProtocolLeadSchema.nullable(),
  after: LeadAfterAccessSchema.nullable(),
});
export type LeadState = z.infer<typeof LeadStateSchema>;

export const LeadLookupQuerySchema = z.object({
  q: z.string().trim().min(LOOKUP_QUERY_MIN).max(LOOKUP_QUERY_MAX),
});

/** Пользователь в разделе «Пользователи». */
export const LeadLookupUserSchema = z.object({
  id: IdSchema,
  name: z.string(),
  email: z.string(),
  /** Ведёт этот протокол сейчас. */
  current: z.boolean(),
  /** Внешний пользователь (022): ведущим быть не может (ACC-007). */
  external: z.boolean(),
});
export type LeadLookupUser = z.infer<typeof LeadLookupUserSchema>;

/** Человек в разделе «Из справочника — нет учётной записи». */
export const LeadLookupPersonSchema = z.object({
  personId: IdSchema,
  name: z.string(),
  email: z.string(),
  /** Организация основного места работы. */
  organization: z.string().nullable(),
  standing: EmailStandingSchema,
});
export type LeadLookupPerson = z.infer<typeof LeadLookupPersonSchema>;

export const LeadLookupResponseSchema = z.object({
  users: z.array(LeadLookupUserSchema).max(LEAD_LOOKUP_LIMIT),
  people: z.array(LeadLookupPersonSchema).max(LEAD_LOOKUP_LIMIT),
});
export type LeadLookupResponse = z.infer<typeof LeadLookupResponseSchema>;

/**
 * Смена ведущего (014: FR-033; 038: FR-008, FR-010, FR-019). `expectedLeadId` — кого окно считает
 * ведущим (null — ведущего нет); нет поля — сверки нет, последняя запись побеждает (014). Снять
 * ведущего (`userId: null`) можно только «Отменить» после первого назначения (research Р7).
 */
export const SetLeadBodySchema = z
  .strictObject({
    userId: IdSchema.nullable(),
    expectedLeadId: IdSchema.nullable().optional(),
    /** «Отменить»: без события, письма и уведомления; журнал с пометкой. */
    undo: z.boolean().optional(),
  })
  .refine((body) => body.userId !== null || body.undo === true, {
    message: 'Снять владельца можно только отменой назначения',
    path: ['userId'],
  });
export type SetLeadBody = z.infer<typeof SetLeadBodySchema>;

export const SetLeadResponseSchema = z.object({
  lead: ProtocolLeadSchema.nullable(),
  previous: ProtocolLeadSchema.nullable(),
  /** false — назначили того, кто уже ведёт: ничего не изменено. */
  changed: z.boolean(),
});
export type SetLeadResponse = z.infer<typeof SetLeadResponseSchema>;

/** POST /api/protocols/{id}/lead/user (FR-011 – FR-013; research Р8). */
export const CreateLeadBodySchema = z.strictObject({
  fullName: FullNameInputSchema,
  email: EmailInputSchema,
  /** Человек справочника из раздела «Из справочника — нет учётной записи». */
  personId: IdSchema.optional(),
  expectedLeadId: IdSchema.nullable(),
});
export type CreateLeadBody = z.infer<typeof CreateLeadBodySchema>;

export const CreateLeadResponseSchema = z.object({
  userId: IdSchema,
  lead: ProtocolLeadSchema,
  previous: ProtocolLeadSchema.nullable(),
  /** Номер письма назначения — номер события protocol.lead_assigned (как 035). */
  letterId: IdSchema,
  /** Учётная запись связана с человеком справочника. */
  personLinked: z.boolean(),
});
export type CreateLeadResponse = z.infer<typeof CreateLeadResponseSchema>;
